import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { PoButtonModule } from '@po-ui/ng-components';
import { AtsUiLibModule, LoaderService } from 'ats-ui-lib';
import { JobApplicationService } from '../../root';

interface RouteParams {
  proposalLetterId: string;
  secret: string;
  valid: true;
}

@Component({
  selector: 'ats-accept-proposal-letter',
  imports: [PoButtonModule, RouterModule, TranslateModule, AtsUiLibModule],
  templateUrl: './job-application-proposal-letter-accept.component.html',
  styleUrl: './job-application-proposal-letter-accept.component.css',
})
export class AcceptProposalLetterComponent implements OnInit {
  status = signal<'accepted' | 'inactive' | null>(null);
  confirmationResult = computed<{
    mainImage: string;
    title: string;
    subtitle: string;
  } | null>(() => {
    const basePath = 'assets/images';
    switch (this.status()) {
      case 'accepted':
        return {
          mainImage: `${basePath}/banner-professional-purpose.svg`,
          title: 'l-recommendation-confirmation-proposal-letter-accept-title',
          subtitle: 'l-recommendation-confirmation-proposal-letter-accept-subtitle',
        };
      case 'inactive':
        return {
          mainImage: `${basePath}/imagem_pessoa_sentada.svg`,
          title: 'l-recommendation-confirmation-proposal-letter-error-title',
          subtitle: 'l-recommendation-confirmation-proposal-letter-error-subtitle',
        };
      default:
        return null;
    }
  });

  constructor(
    private jobApplicationService: JobApplicationService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private loaderService: LoaderService,
  ) {}

  async ngOnInit(): Promise<void> {
    const params = this.getParamsFromRoute();
    if (!params.valid) {
      await this.goToHome();
      return;
    }
    await this.decide(params);
  }

  private async decide(params: RouteParams): Promise<void> {
    try {
      this.loaderService.show();
      await this.jobApplicationService.acceptJobApplicationProposalLetter(
        params.proposalLetterId,
        params.secret,
      );
      this.status.set('accepted');
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 404) {
        this.status.set('inactive');
      } else {
        await this.goToHome();
        throw error;
      }
    } finally {
      this.loaderService.hide();
    }
  }

  private getParamsFromRoute(): RouteParams | { valid: false } {
    const activatedRouteSnapshot = this.activatedRoute.snapshot;
    const proposalLetterId = activatedRouteSnapshot.paramMap.get('id')?.toString();
    const secret = activatedRouteSnapshot.paramMap.get('secret')?.toString();
    const isInvalidRoute = proposalLetterId == null || secret == null;
    if (isInvalidRoute) {
      return { valid: false };
    }
    return { proposalLetterId, secret, valid: true };
  }

  async goToHome(): Promise<void> {
    await this.router.navigate(['register-user']);
  }
}
